import { useEffect, useState } from 'react';
import { createConsentSubmission, getConsent, type Me } from '../../api/endpoints.js';
import { useAction, useApi, useIntentKey } from '../../api/hooks.js';
import { goStep } from '../../flow/nav.js';
import { Failure } from '../../ui/kit.js';
import { FlowScreen, FlowSub, FlowTitle, IconCard, PrimaryButton } from '../../ui/flow.js';

/**
 * Mobile screen 13 — the consent gate. Consent is always signed BEFORE any
 * biometric data is captured; the selfie is the biometric data. Continue
 * creates the DocuSeal submission and opens the signing URL in its own tab —
 * the URL is a bearer capability, never shown as text and never stored — and
 * this screen polls the consent record until the webhook lands, then moves on
 * by itself. The mobile app polls /check-user every 2 s for the same reason.
 */
export function ConsentGateStep({ me, reloadMe }: { me: Me; reloadMe: () => void }) {
  const [consentId, setConsentId] = useState<string | null>(null);
  const intent = useIntentKey();

  const create = useAction(createConsentSubmission, (result) => {
    setConsentId(result.consent_id);
    window.open(result.signing_url, '_blank', 'noopener,noreferrer');
  });

  const status = useApi(() => getConsent(consentId ?? ''), [consentId], {
    skip: consentId === null,
    pollMs: 2000,
  });

  const alreadySigned = me.onboarding.consent_signed;
  const signedNow = status.data?.status === 'signed';
  useEffect(() => {
    if (alreadySigned || signedNow) {
      reloadMe();
      goStep('selfie', { replace: true });
    }
  }, [alreadySigned, signedNow, reloadMe]);

  const blocked = me.consent_eligibility.blocked_reason;

  return (
    <FlowScreen
      progress={80}
      onBack={() => window.history.back()}
      onClose={() => window.history.back()}
      footer={
        <PrimaryButton
          onClick={() => create.run(me.person.person_id, intent.key)}
          disabled={create.pending || (consentId !== null && !signedNow)}
        >
          {create.pending
            ? 'Opening consent…'
            : consentId !== null
              ? 'Waiting for your signature…'
              : 'Continue'}
        </PrimaryButton>
      }
    >
      <FlowTitle>Let&rsquo;s get your selfie</FlowTitle>
      <FlowSub>
        Please ensure that we get a clear shot of your face. Remove any headgear that you might be
        wearing.
      </FlowSub>

      <div className="option-stack">
        <IconCard
          icon={
            <svg width="26" height="26" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M8 1.8 13 4v4.5c0 2.9-2 4.5-5 5.5-3-1-5-2.6-5-5.5V4z" stroke="#380E99" strokeWidth="1.4" />
            </svg>
          }
        >
          Your selfie is encrypted and stored securely
        </IconCard>
        <IconCard
          icon={
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="5" y="10" width="14" height="10" rx="2" stroke="#380E99" strokeWidth="1.8" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="#380E99" strokeWidth="1.8" />
            </svg>
          }
        >
          We never share your photos with third parties
        </IconCard>
        <IconCard
          icon={
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" stroke="#380E99" strokeWidth="1.8" />
              <circle cx="12" cy="12" r="2.6" stroke="#380E99" strokeWidth="1.8" />
            </svg>
          }
        >
          Used only for identity verification and likeness monitoring
        </IconCard>
      </div>

      <p className="flow-note">
        <strong>Privacy Promise:</strong> All selfie data is protected with end-to-end encryption
        and will never be shared or used for any purpose other than protection.
      </p>

      {blocked !== null && (
        <p className="flow-error">
          Consent is blocked for this account: <code>{blocked}</code>
        </p>
      )}

      {consentId !== null && !signedNow && (
        <div className="score-card">
          <p className="score-card-label">Sign in the tab that opened</p>
          <p className="flow-sub" style={{ margin: 0 }}>
            The biometric consent form opened in another tab. Sign it there and come back — this
            screen checks every 2 seconds and continues on its own. The signature is recorded
            through DocuSeal&rsquo;s webhook, so it can take a few seconds to appear.
          </p>
        </div>
      )}

      {create.error !== null && (
        <Failure
          error={create.error}
          hint={
            {
              DOB_REQUIRED:
                'Your date of birth is missing — go back to the profile step and set it; it decides who must sign.',
              UNDER_MIN_AGE: 'Below the enrolment age floor — this account cannot enrol a face.',
              GUARDIANSHIP_REQUIRED: 'A minor subject needs a guardian signer.',
              SUBJECT_EMAIL_UNVERIFIED: 'Your email must be verified before consent can be created.',
            }[create.error.code]
          }
        />
      )}
      {status.error !== null && <Failure error={status.error} />}
    </FlowScreen>
  );
}
